import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CardConfig} from '@app/model/dcp-config-service/card-config';
import {DcpSetupIntent} from '@app/model/stripe/setup-intent';
import {DpaoService} from '@app/services/dpao/dpao.service';
import {DcpGlobalMessageService, DcpLoggingService, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {loadStripe, SetupIntent, Stripe, StripePaymentElement, StripePaymentElementOptions} from '@stripe/stripe-js';
import {StripeElements, StripeElementsOptionsClientSecret} from '@stripe/stripe-js/types/stripe-js/elements-group';
import {Observable, of} from 'rxjs';
import {first, map, switchMap, take, tap} from 'rxjs/operators';
import {DcpCheckoutPaymentService} from '../../services/dcp-checkout-payment.service';
import {DcpStripeService} from '../../services/stripe/dcp-stripe.service';
import {StripeNewCcConfigService} from './stripe-new-cc-config.service';
import {TranslationService} from '@spartacus/core';
import {DcpCcPspService} from '@app/payment/services/dcp-cc-psp.service';

@UntilDestroy()
@Component({
  selector: 'dcp-stripe-add-new-cc',
  templateUrl: './stripe-add-new-cc.component.html',
  styleUrls: ['./stripe-add-new-cc.component.scss']
})
export class StripeAddNewCcComponent implements OnInit {

  elementsOptions: StripeElementsOptionsClientSecret;
  stripePaymentElementOptions: StripePaymentElementOptions;

  // lazy loaded
  stripeRef: Stripe;
  stripeElements: StripeElements;
  paymentElement: StripePaymentElement;
  stripePublishableKey;
  cardConfig: CardConfig;

  isOpen = false;
  isPaymentElementLoaded = false;
  errorMessage: string;
  isSaveCardInProgress = false;
  formIncomplete = true;

  @Input()
  isClosable = false;
  registerCreditCardInDpaoFeatureToggle = false;

  constructor(
    private dcpStripeService: DcpStripeService,
    private cdr: ChangeDetectorRef,
    private userAccountFacade: UserAccountFacade,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private dcpLoggingService: DcpLoggingService,
    private dpaoService: DpaoService,
    private featureTogglesService: FeatureTogglesService,
    private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
    private stripeNewCcConfigService: StripeNewCcConfigService,
    private translationService: TranslationService,
    private dcpCcPspService: DcpCcPspService,
  ) {
  }

  ngOnInit(): void {
    this.stripePaymentElementOptions = this.stripeNewCcConfigService.getPaymentElementOptions();
    this.subscribeToFeatureToggle();
    this.subscribeToCurrentPaymentType();
    this.subscribeToAddNewCreditCardComponentOpened();
  }

  private subscribeToFeatureToggle(): void {
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.registerCreditCardInDPAO).pipe(untilDestroyed(this)).subscribe(enabled => {
      this.registerCreditCardInDpaoFeatureToggle = enabled;
    });
  }

  private subscribeToCurrentPaymentType(): void {
    this.dcpCheckoutPaymentService.getCurrentPaymentType().pipe(untilDestroyed(this)).subscribe(() => {
      if (this.isClosable) {
        this.close();
      } else {
        this.clearStripeFields();
      }
    });
  }

  private subscribeToAddNewCreditCardComponentOpened(): void {
    this.dcpStripeService.isAddNewCreditCardComponentOpened().pipe(untilDestroyed(this)).subscribe((isOpen) => {
      this.isOpen = isOpen;
      this.cdr.detectChanges();
      if (this.isOpen) {
        if (!this.stripeRef) {
          this.lazyLoadStripe();
        } else if (this.shouldGetNewSetupIntent()) {
          this.newSetupIntentSecret();
        }
      } else {
        this.dcpCcPspService.setErrorMessageStatus(false);
      }
    });
  }

  shouldGetNewSetupIntent() {
    return !this.elementsOptions.clientSecret;
  }

  lazyLoadStripe() {
    let isStripeConnectedAccountEnabled: boolean;
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.stripeConnectedAccount).pipe(
      take(1),
      tap(enabled => isStripeConnectedAccountEnabled = enabled),
      switchMap(() => this.dcpStripeService.getStripeCardConfig()),
      untilDestroyed(this)).subscribe((cardConfig) => {
      this.cardConfig = cardConfig;
      this.stripePublishableKey = this.cardConfig.merchantId;
      const connectedAccountId = cardConfig.connectedAccountId;
      loadStripe(this.stripePublishableKey, {
        betas: ['blocked_card_brands_beta_2'],
        ...(isStripeConnectedAccountEnabled && connectedAccountId && {stripeAccount: connectedAccountId})
      }).then(stripe => {
        this.stripeRef = stripe;
        this.newSetupIntentSecret();
      });
    });
  }

  newSetupIntentSecret() {
    this.dcpStripeService.fetchSetupIntentSecret().pipe(take(1), untilDestroyed(this)).subscribe((clientSecret) => {
      this.elementsOptions = this.stripeNewCcConfigService.getElementsOptions(clientSecret, this.cardConfig.allowedCardBrands);
      this.cdr.detectChanges();
      this.mountStripe();
    });
  }

  mountStripe() {
    this.stripeElements = this.stripeRef.elements(this.elementsOptions);
    this.paymentElement = this.stripeElements.create('payment', this.stripePaymentElementOptions);
    this.paymentElement.mount('#payment-element');
    this.paymentElement.on('ready', () => {
      this.paymentElementLoaded();
      this.cdr.detectChanges();
    });
    this.paymentElement.on('change', (change) => {
      this.formIncomplete = !change.complete;
      this.cdr.detectChanges();
    });
  }

  saveCreditCard() {
    this.errorMessage = undefined;
    this.isSaveCardInProgress = true;
    let currentUser;
    this.dpaoService.hideError();

    this.userAccountFacade.get().pipe(
      take(1),
      switchMap(user => {
        currentUser = user;
        return this.stripeRef.confirmSetup({
            elements: this.stripeElements,
            confirmParams: {
              expand: ['payment_method']
            },
            redirect: 'if_required',
          }
        );
      }),
      switchMap(result => this.handleStripeResponse(result)),
      switchMap(setupIntentWithPaymentMethod => this.registerCreditCardInDpao(setupIntentWithPaymentMethod.setupIntent)),
      untilDestroyed(this)
    ).subscribe((setupIntent: SetupIntent) => {
        this.dcpStripeService.saveCreditCardDetails(currentUser, setupIntent).pipe(untilDestroyed(this)).subscribe(result => {
            this.isSaveCardInProgress = false;
            this.isPaymentElementLoaded = false;
            this.elementsOptions.clientSecret = undefined; // client secret can be used only once to add new CC
            this.dcpCcPspService.setErrorMessageStatus(false);
            this.dcpStripeService.closeAddNewCreditCardComponent();
          },
          error => {
            this.newSetupIntentSecret(); // create a new setup intent after /fullPaymentDetails failure
            this.translationService.translate('common.errors.somethingWrong').pipe(first(), untilDestroyed(this)).subscribe(msg => {
              this.errorMessage = msg;
              this.isSaveCardInProgress = false;
              this.cdr.detectChanges();
            })
            this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
          }
        )
      },
      (error) => {
        this.dpaoService.displayError();
        this.isSaveCardInProgress = false;
        this.newSetupIntentSecret(); // create a new setup intent after dpao failure
        this.dcpGlobalMessageService.addMessage('DpaoError ' + error.message, this?.constructor?.name, 'errorLogging');
      }
    );
  }

  handleStripeResponse(stripeResponse: { setupIntent?; error? }): Observable<any> {
    if (stripeResponse.error) {
      this.errorMessage = stripeResponse.error.message;
      this.isSaveCardInProgress = false;
      this.dcpGlobalMessageService.addMessage('StripeError ' + stripeResponse.error.message, this?.constructor?.name, 'errorLogging');
      this.cdr.detectChanges();
      throw stripeResponse.error;
    } else {
      const message = {
        message: 'Credit card was saved properly',
        body: stripeResponse
      };
      this.dcpLoggingService.info(message, this?.constructor?.name);
    }
    return of(stripeResponse);
  }

  registerCreditCardInDpao(setupIntent: DcpSetupIntent): Observable<SetupIntent> {
    if (this.registerCreditCardInDpaoFeatureToggle) {
      return this.dpaoService.registerCreditCard(setupIntent).pipe(
        map(dpaoSubscriptionId => {
          if (dpaoSubscriptionId) {
            setupIntent.dpaoSubscriptionId = dpaoSubscriptionId;
          }
          return setupIntent;
        })
      );
    } else {
      return of(setupIntent);
    }
  }

  paymentElementLoaded() {
    this.isPaymentElementLoaded = true;
  }

  clearStripeFields() {
    this.paymentElement?.clear();
    this.errorMessage = undefined;
    this.cdr.detectChanges();
  }

  close() {
    this.clearStripeFields();
    this.dcpStripeService.closeAddNewCreditCardComponent();
  }
}
